import styled from 'styled-components';
import {
  fleetLeading,
  fleetType,
} from '@entities/fleet';
import {
  inputNumber,
  roundDecimal,
  trimDecimal,
  type LimitCellView,
} from '@entities/prc';
import { TextButton } from '@widgets/FleetRegistry/ui';
import { formatNumber } from '../../format';
import { NumTable } from '../../ui';

export const Table = styled(NumTable)`
  /* Скрытые подписи ячеек (position: absolute) держатся внутри таблицы и её
     прокрутки — иначе на узком экране они расширяют саму панель. */
  position: relative;

  td.c-name {
    min-width: 200px;
    max-width: 300px;
    overflow-wrap: anywhere;
  }

  td.c-res {
    min-width: 120px;
    max-width: 200px;
  }

  td.c-text {
    min-width: 88px;
    max-width: 160px;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
  }

  td.cell {
    min-width: 116px;
  }

  td.row-no {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.micro};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  tr.percent > td,
  tr.percent > th {
    background: ${({ theme }) => theme.colors.surface};
  }

  .pending {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-style: italic;
  }
`;

export const CellNote = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${fleetType.micro};
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: right;
  white-space: nowrap;
`;

export const FormulaMark = styled.span`
  flex: none;
  width: 12px;
  font-family: ${({ theme }) => theme.font.mono};
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

/** Поле ячейки с меткой «ƒ» слева: метка не сдвигает поля соседних строк. */
export const InputRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};

  & > input {
    flex: 1 1 auto;
  }
`;

export const Resource = styled.span`
  display: block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  & > .sub {
    display: block;
    margin-top: 2px;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &[data-text='true'] {
    white-space: normal;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const TableNote = styled.p`
  margin: ${({ theme }) => `${theme.spacing(4)} 0 ${theme.spacing(2)}`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

export const Restore = styled(TextButton)`
  display: block;
  margin: 2px 0 0 auto;
  font-size: ${fleetType.micro};
`;

export const ErrorList = styled.ul`
  flex: 1 1 100%;
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: ${fleetType.small};
`;

export const BASIS: Readonly<Record<string, string>> = {
  mileage: 'Количество в год — от годового пробега из паспорта модели',
  hours: 'Количество в год — от годового режима работы, маш.-ч',
  constant: 'Основа шапки — число, записанное в книге',
};

/**
 * Значение ячейки для поля ввода: число с запятой и разрядами. Результат
 * формулы книги (0,333333…) — до четырёх знаков: в расчёт идёт точное
 * значение сервера, а в поле — читаемое; правкой считается только ввод.
 */
export function inputOf(cell: LimitCellView): string {
  if (!cell.numeric || cell.value === null) return '';
  const shown = cell.formula ? (roundDecimal(cell.value, 4) ?? cell.value) : cell.value;
  return inputNumber(trimDecimal(shown));
}

export function cellText(cell: LimitCellView, max = 2): string {
  if (cell.value === null) return '—';
  return cell.numeric ? formatNumber(cell.value, max) : cell.value;
}
